import { Injectable, signal } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { take } from 'rxjs';
import { FileService } from '../file/file.service';

@Injectable({
  providedIn: 'root',
})
export class ScreenRecordingService {
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];

  private screenStream?: any;

  constructor(
    private fileService: FileService,
    private messageService: NzMessageService
  ) {}

  async startRecording(micStream: MediaStream) {
    // Capture the mic audio

    this.isUploading.set(undefined);

    const audioContext = new AudioContext();

    const screenSource = audioContext.createMediaStreamSource(this.screenStream);
    const micSource = audioContext.createMediaStreamSource(micStream);

    const destination = audioContext.createMediaStreamDestination();
    screenSource.connect(destination);
    micSource.connect(destination);

    // Combine both streams (screen and mic)
    const combinedStream = new MediaStream([
      ...this.screenStream.getVideoTracks(),
      ...destination.stream.getAudioTracks(),
    ]);

    this.mediaRecorder = new MediaRecorder(combinedStream, { mimeType: 'video/webm' });

    this.mediaRecorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    });

    this.mediaRecorder?.start(250);
  }

  async grantPermission(): Promise<void> {
    const displayMediaOptions = {
      video: {
        cursor: 'always',
        displaySurface: 'browser',
        height: 720,
        frameRate: 15,
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 44100,
      },
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      systemAudio: 'include',
      surfaceSwitching: 'exclude',
      monitorTypeSurfaces: 'exclude',
    };

    try {
      // Capture the screen with system audio
      this.screenStream = await (navigator.mediaDevices as any).getDisplayMedia(displayMediaOptions);
    } catch (err) {
      console.error('Error: ' + err);
    }
  }

  isUploading = signal<boolean | undefined>(undefined);

  stopRecordingAndSaveToWebm(fileUrl: string) {
    if (this.mediaRecorder) {
      this.isUploading.set(true);
      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
        this.recordedChunks = [];

        const messageId = this.messageService.loading('Uploading video... (Please keep the browser open)', {
          nzDuration: 0,
        }).messageId;

        this.screenStream?.getTracks().forEach((track: any) => track.stop());

        this.fileService
          .uploadBlobToS3$(`${fileUrl}`, blob)
          .pipe(take(1))
          .subscribe(() => {
            this.messageService.remove(messageId);

            this.isUploading.set(false);
          });
      };

      this.mediaRecorder.stop();
    }
  }

  downloadRecording(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }
}
